import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  Grid, 
  Button, 
  Chip,
  AppBar,
  Toolbar
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/gemistruct-logo.png';
import wordmark from '../assets/gemistruct-wordmark.png';

export default function Privacy() {
  const navigate = useNavigate();

  return (
    <>
      {/* Header */}
      <AppBar 
        position="static" 
        sx={{ 
          backgroundColor: 'white',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          zIndex: 10,
        }} 
        elevation={0}
      >
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            {/* Clickable Logo that links to Home */}
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                cursor: 'pointer',
                '&:hover': { opacity: 0.8 }
              }}
              onClick={() => navigate('/')}
            >
              <img src={logo} alt="Gemistruct Logo" style={{ height: 48, marginRight: 16 }} />
              <img src={wordmark} alt="Gemistruct" style={{ height: 36 }} />
            </Box>
          </Box>
          
          {/* Navigation Links */}
          <Box sx={{ display: 'flex', gap: 2, mr: 3 }}>
            <Button 
              sx={{ 
                color: 'text.primary',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                }
              }}
              onClick={() => navigate('/consulting')}
            >
              CONSULTING SERVICES
            </Button>
            <Button 
              sx={{ 
                color: 'text.primary',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: 'secondary.main',
                  color: 'white',
                }
              }}
              onClick={() => navigate('/engineering-tools')}
            >
              ENGINEERING TOOLS
            </Button>
            <Button 
              sx={{ 
                color: 'text.primary',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                }
              }}
              onClick={() => {
                navigate('/#about');
                setTimeout(() => {
                  document.getElementById('about')?.scrollIntoView({ 
                    behavior: 'smooth',
                    block: 'start'
                  });
                }, 100);
              }}
            >
              ABOUT US
            </Button>
            <Button 
              sx={{ 
                color: 'text.primary',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                }
              }}
              onClick={() => {
                navigate('/#contact');
                setTimeout(() => {
                  document.getElementById('contact')?.scrollIntoView({ 
                    behavior: 'smooth',
                    block: 'start'
                  });
                }, 100);
              }}
            >
              CONTACT
            </Button>
          </Box>
          
          <Button 
            color="primary" 
            variant="contained" 
            sx={{ 
              fontWeight: 600,
              '&:hover': {
                transform: 'scale(1.05)',
                transition: 'transform 0.2s ease-in-out',
              }
            }}
            onClick={() => navigate('/signin')}
          >
            Sign In
          </Button>
        </Toolbar>
      </AppBar>

      {/* Content */}
      <Container maxWidth="md">
        <Box sx={{ py: 8 }}>
          <Paper elevation={0} sx={{ p: 4 }}>
            <Typography variant="h3" gutterBottom fontWeight={700}>
              Gemistruct Privacy Policy
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              Effective Date: {new Date().toLocaleDateString()}
            </Typography>
            
            <Typography paragraph sx={{ mt: 4 }}>
              Gemistruct Pty Ltd ("we", "our", "us") is committed to protecting your privacy in accordance with the Privacy Act 1988 (Cth) and the Australian Privacy Principles (APPs). This Privacy Policy explains how we collect, use, store, and disclose personal information in relation to our structural engineering consulting services and online structural design tools.
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 600 }}>
              1. Information We Collect
            </Typography>
            <Typography paragraph>
              We may collect personal information that is reasonably necessary for our business purposes, including:
            </Typography>
            <Typography paragraph>
              • Name, company name, and contact details (email, phone, address).
            </Typography>
            <Typography paragraph>
              • Project information provided for engineering consulting and drafting services.
            </Typography>
            <Typography paragraph>
              • Account details (if you register to use our online tools).
            </Typography>
            <Typography paragraph>
              • Technical data when you use our website or tools (e.g. browser type, IP address, usage statistics).
            </Typography>
            <Typography paragraph>
              We do not collect sensitive information unless it is directly relevant and you provide consent.
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 600 }}>
              2. How We Collect Information
            </Typography>
            <Typography paragraph>
              We collect personal information directly from you when you:
            </Typography>
            <Typography paragraph>
              • Contact us about engineering consulting or drafting services.
            </Typography>
            <Typography paragraph>
              • Provide project documentation or drawings.
            </Typography>
            <Typography paragraph>
              • Register or use our structural design tools.
            </Typography>
            <Typography paragraph>
              • Subscribe to updates or communicate with us via email or our website.
            </Typography>
            <Typography paragraph>
              We may also collect limited technical data automatically through cookies or analytics tools to improve website functionality.
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 600 }}>
              3. How We Use Information
            </Typography>
            <Typography paragraph>
              We use the personal information we collect to:
            </Typography>
            <Typography paragraph>
              • Provide structural engineering consulting and drafting services.
            </Typography>
            <Typography paragraph>
              • Operate and improve our structural design tools.
            </Typography>
            <Typography paragraph>
              • Communicate with you about projects, services, and updates.
            </Typography>
            <Typography paragraph>
              • Comply with legal and regulatory requirements.
            </Typography>
            <Typography paragraph>
              • Maintain business records and manage client relationships.
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 600 }}>
              4. Disclosure of Information
            </Typography>
            <Typography paragraph>
              We do not sell or rent your personal information. We may disclose personal information only when:
            </Typography>
            <Typography paragraph>
              • Required by law or regulatory authorities.
            </Typography>
            <Typography paragraph>
              • Necessary to consultants, contractors, or service providers engaged by us (e.g. IT, hosting, insurance), subject to confidentiality.
            </Typography>
            <Typography paragraph>
              • You provide consent for disclosure.
            </Typography>
            <Typography paragraph>
              All disclosures are made in line with the Australian Privacy Principles.
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 600 }}>
              5. Data Storage and Security
            </Typography>
            <Typography paragraph>
              We take reasonable steps to protect personal information from misuse, interference, loss, unauthorised access, modification, or disclosure.
            </Typography>
            <Typography paragraph>
              • Project files and client documents are stored securely.
            </Typography>
            <Typography paragraph>
              • Access to online tools is password-protected.
            </Typography>
            <Typography paragraph>
              • Where third-party cloud services are used, we ensure appropriate safeguards are in place.
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 600 }}>
              6. Access and Correction
            </Typography>
            <Typography paragraph>
              You may request access to the personal information we hold about you and request corrections if you believe it is inaccurate, out-of-date, incomplete, or misleading. Requests can be made in writing (see contact details below).
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 600 }}>
              7. Overseas Disclosure
            </Typography>
            <Typography paragraph>
              Our services are primarily based in Australia. However, some data (e.g. cloud hosting, email services) may be stored on servers located outside Australia. Where this occurs, we take steps to ensure your information remains protected in accordance with Australian Privacy Principles.
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 600 }}>
              8. Use of Cookies and Analytics
            </Typography>
            <Typography paragraph>
              Our website and tools may use cookies and analytics to improve functionality and user experience. You can choose to disable cookies in your browser settings, but this may affect website or tool functionality.
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 600 }}>
              9. Contact Us
            </Typography>
            <Typography paragraph>
              If you have any questions or concerns about this Privacy Policy or how your personal information is handled, please contact us:
            </Typography>
            <Typography paragraph sx={{ fontWeight: 600 }}>
              Gemistruct Pty Ltd
            </Typography>
            <Typography paragraph>
              Email: <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>
                <a href="mailto:info@gemistruct.com" style={{ textDecoration: 'none', color: 'inherit' }}>
                  info@gemistruct.com
                </a>
              </Box>
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 600 }}>
              10. Complaints
            </Typography>
            <Typography paragraph>
              If you believe we have breached the Australian Privacy Principles, please contact us using the details above. We will investigate and respond to your complaint promptly. If you are not satisfied, you may contact the Office of the Australian Information Commissioner (OAIC) at{' '}
              <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>
                <a href="https://www.oaic.gov.au" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
                  www.oaic.gov.au
                </a>
              </Box>
            </Typography>

            <Box sx={{ mt: 6, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography color="text.secondary">
                This policy is effective as of the date listed above and may be updated from time to time.
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Container>

      {/* Footer */}
      <Box sx={{ bgcolor: 'grey.900', color: 'grey.100', py: 4 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                {/* Company name in text instead of logo */}
                <Typography variant="h5" fontWeight={700} sx={{ mb: 1, color: 'white' }}>
                  Gemistruct
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: 'grey.300' }}>
                  Professional consulting and cloud-based engineering tools for Australian structural engineering industry.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                   <Chip label="CPEng" size="small" sx={{ 
                     color: 'white', 
                     borderColor: 'white', 
                     '& .MuiChip-label': { color: 'white' }
                   }} variant="outlined" />
                   <Chip label="RPEQ" size="small" sx={{ 
                     color: 'white', 
                     borderColor: 'white', 
                     '& .MuiChip-label': { color: 'white' }
                   }} variant="outlined" />
                   <Chip label="NER" size="small" sx={{ 
                     color: 'white', 
                     borderColor: 'white', 
                     '& .MuiChip-label': { color: 'white' }
                   }} variant="outlined" />
                 </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>Quick Links</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button 
                  color="inherit" 
                  sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                  onClick={() => navigate('/')}
                >
                  Home
                </Button>
                <Button 
                  color="inherit" 
                  sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                  onClick={() => navigate('/consulting')}
                >
                  Consulting Services
                </Button>
                <Button 
                  color="inherit" 
                  sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                  onClick={() => navigate('/engineering-tools')}
                >
                  Engineering Tools
                </Button>
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ borderTop: 1, borderColor: 'grey.800', pt: 3, mt: 3, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'grey.100' }}>
              Copyright © {new Date().getFullYear()} GEMISTRUCT PTY LTD ABN: 80 684 710 594{' '}
              <Button 
                color="inherit" 
                sx={{ 
                  textTransform: 'none', 
                  fontSize: '0.875rem',
                  color: 'grey.100',
                  '&:hover': { color: 'white' },
                  p: 0,
                  minWidth: 'auto'
                }}
                onClick={() => navigate('/privacy')}
              >
                Privacy
              </Button>
              {' '}|{' '}
              <Button 
                color="inherit" 
                sx={{ 
                  textTransform: 'none', 
                  fontSize: '0.875rem',
                  color: 'grey.100',
                  '&:hover': { color: 'white' },
                  p: 0,
                  minWidth: 'auto'
                }}
                onClick={() => navigate('/terms')}
              >
                Terms & Conditions
              </Button>
            </Typography>
          </Box>
        </Container>
      </Box>
    </>
  );
}
